import { type Latka, matches, tokenize } from '@/components/sklad/types';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Výběr látky z katalogu (zrcadlo Tabidoo) — dropdown s náhledy a tolerantním
 * hledáním přes kód, název i druh.
 */
export function LatkaPicker({
  value,
  options,
  loading,
  onChange,
}: {
  value: Latka | null;
  options: Latka[];
  loading: boolean;
  onChange: (l: Latka) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Klik mimo dropdown ho zavře.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const tokens = tokenize(query);
    return options.filter((o) => matches(`${o.code} ${o.name} ${o.type}`, tokens));
  }, [options, query]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 text-left text-sm bg-surface border border-border rounded-lg px-2.5 py-2 hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition-all cursor-pointer"
      >
        {value ? (
          <>
            <Thumb url={value.image} size={32} />
            <span className="flex-1 min-w-0 truncate text-text font-medium">{value.name}</span>
          </>
        ) : (
          <span className="flex-1 text-text-muted">
            {loading ? 'Načítám látky…' : '— vyber látku —'}
          </span>
        )}
        <span className="text-text-muted shrink-0">▾</span>
      </button>

      {open && (
        <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded-lg shadow-card-hover overflow-hidden">
          <div className="p-2 border-b border-border-light">
            <input
              // biome-ignore lint/a11y/noAutofocus: dropdown se otevírá kliknutím, hledání je jeho účel
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Hledat kód, název nebo druh…"
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
            />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading && (
              <div className="px-3 py-6 text-center text-sm text-text-muted">Načítám…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-text-muted">Nic nenalezeno</div>
            )}
            {!loading &&
              filtered.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => {
                    onChange(o);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 text-left hover:bg-surface-hover transition-colors cursor-pointer"
                >
                  <Thumb url={o.image} size={36} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-text truncate">{o.name}</span>
                    <span className="block text-[11px] text-text-muted truncate">
                      {o.code}
                      {o.type && ` · ${o.type}`}
                    </span>
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Thumb({ url, size }: { url: string; size: number }) {
  const style = { width: size, height: size };
  if (!url) return <div style={style} className="rounded bg-bg border border-border shrink-0" />;
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      style={style}
      className="rounded object-cover border border-border bg-bg shrink-0"
    />
  );
}
